import type { SandBoxInterface, clonedWindowType } from '@micro-app/types'
import bindFunction from './bind_function'
import { unique, setCurrentAppName, defer, getEffectivePath } from '../libs/utils'
import effect, { effectDocumentEvent, releaseEffectDocumentEvent } from './effect'
import { EventCenterForMicroApp } from '../interact'
import microApp from '../micro_app'

/* eslint-disable camelcase */
type injectDataType = {
  __micro_app_environment__: boolean
  __micro_app_name__: string
  __full_public_path__: string
  microApp: EventCenterForMicroApp
  baseurl: string
}

// 一些同时赋值到proxy和原window上的变量
// TODO：可以扩展，用户自行补充一些变量
const propertyKeyWhiteList: PropertyKey[] = [
  'System',
  '__cjsWrapper',
  '__REACT_ERROR_OVERLAY_GLOBAL_HOOK__', // react development react hot reload
]

// 一些只能赋值到原window上的变量
const escapeSetterKeyList: PropertyKey[] = [
  'location',
]

const unscopables = {
  undefined: true,
  Array: true,
  Object: true,
  String: true,
  Boolean: true,
  Math: true,
  Number: true,
  Symbol: true,
  parseFloat: true,
  Float32Array: true,
}

/**
 * 宏任务延迟执行，解决vue3的部分渲染问题
 * TODO BUG: 宏任务在频繁切换渲染时容易导致渲染出错
 */
let macroTimer: number
function macroTask (fn: TimerHandler): void {
  if (macroTimer) clearTimeout(macroTimer)
  macroTimer = setTimeout(fn, 0)
}

export default class SandBox implements SandBoxInterface {
  static activeCount = 0 // 正在运行的沙盒数量
  active = false // 当前沙盒运行状态
  proxyWindow: WindowProxy & injectDataType
  releaseEffect: CallableFunction

  constructor (appName: string, url: string, macro: boolean) {
    const rawWindow = window as clonedWindowType
    const clonedWindow = {} as Window // TODO: iframe document
    const descriptorTargetMap = new Map<PropertyKey, 'target' | 'rawWindow'>()
    const hasOwnProperty = (key: PropertyKey) => clonedWindow.hasOwnProperty(key) || rawWindow.hasOwnProperty(key)
    // 强行隔离的属性
    const scopeProperties = this.getScopeProperties(appName)
    // 注入全局变量
    this.inject(clonedWindow, appName, url)
    // 重写全局事件监听&定时器
    this.releaseEffect = effect(clonedWindow)

    this.proxyWindow = new Proxy(clonedWindow, {
      get: (target: clonedWindowType, key: PropertyKey): unknown => {
        // 解决with的历史兼容性问题
        if (key === Symbol.unscopables) return unscopables

        if (['window', 'self', 'globalThis'].includes(key as string)) {
          return this.proxyWindow
        }

        if (key === 'top' || key === 'parent') {
          if (rawWindow === rawWindow.parent) { // 不在iframe
            return this.proxyWindow
          }
          return Reflect.get(rawWindow, key) // iframe
        }

        if (key === 'hasOwnProperty') return hasOwnProperty

        if (key === 'document' || key === 'eval') {
          setCurrentAppName(appName)
          // 使用宏任务以解决vue3中元素异步创建的问题
          ;(macro ? macroTask : defer)(() => setCurrentAppName(null))
          switch (key) {
            case 'document':
              return document
            case 'eval':
              return eval // 必须返回eval，否则报错 __webpack_require__ is not defined
          }
        }
        // 只处理jsonpFunction默认值
        if (key === 'webpackJsonp' || scopeProperties.includes(key)) {
          return Reflect.get(target, key)
        }

        if (Reflect.has(target, key)) {
          return Reflect.get(target, key)
        }

        const rawValue = Reflect.get(rawWindow, key)

        return bindFunction(rawWindow, rawValue)
      },
      set: (target: clonedWindowType, key: PropertyKey, value: unknown): boolean => {
        if (this.active) {
          // TODO: react16 key=event 定义次数太多，检查一下
          if (escapeSetterKeyList.includes(key)) {
            Reflect.set(rawWindow, key, value)
          } else if (
            !target.hasOwnProperty(key) &&
            rawWindow.hasOwnProperty(key) &&
            key !== 'webpackJsonp' &&
            !scopeProperties.includes(key)
          ) {
            const descriptor = Object.getOwnPropertyDescriptor(rawWindow, key)
            const { writable, configurable, enumerable } = descriptor!
            if (writable) {
              Object.defineProperty(target, key, {
                configurable,
                enumerable,
                writable,
                value,
              })
            }
          } else {
            Reflect.set(target, key, value)
          }

          if (propertyKeyWhiteList.includes(key) && !Reflect.has(rawWindow, key)) {
            Reflect.set(rawWindow, key, value)
          }
        }

        return true
      },
      has (target: clonedWindowType, key: PropertyKey): boolean {
        if (scopeProperties.includes(key)) return key in target
        return key in unscopables || key in target || key in rawWindow
      },
      getOwnPropertyDescriptor (target: clonedWindowType, key: PropertyKey): PropertyDescriptor|undefined {
        if (target.hasOwnProperty(key)) {
          descriptorTargetMap.set(key, 'target')
          return Object.getOwnPropertyDescriptor(target, key)
        }

        if (rawWindow.hasOwnProperty(key)) {
          descriptorTargetMap.set(key, 'rawWindow')
          const descriptor = Object.getOwnPropertyDescriptor(rawWindow, key)
          if (descriptor && !descriptor.configurable) {
            descriptor.configurable = true
          }
          return descriptor
        }

        return undefined
      },
      defineProperty (target: clonedWindowType, key: PropertyKey, value: PropertyDescriptor): boolean {
        const from = descriptorTargetMap.get(key)
        if (from === 'rawWindow') {
          return Reflect.defineProperty(rawWindow, key, value)
        }
        return Reflect.defineProperty(target, key, value)
      },
      ownKeys (target: clonedWindowType): Array<string | symbol> {
        return unique(Reflect.ownKeys(rawWindow).concat(Reflect.ownKeys(target)))
      },
      deleteProperty (target: clonedWindowType, key: PropertyKey): boolean {
        if (target.hasOwnProperty(key)) {
          return Reflect.deleteProperty(target, key)
        }
        return true
      },
    })
  }

  start (baseurl: string): void {
    if (!this.active) {
      this.active = true
      this.proxyWindow.baseurl = baseurl
      // 解决babel-polyfill@6x重复定义导致失败的问题，还需再验证是否会导致其它问题
      if (window._babelPolyfill) window._babelPolyfill = false
      if (++SandBox.activeCount === 1) {
        effectDocumentEvent()
      }
    }
  }

  stop (): void {
    if (this.active) {
      this.active = false
      this.releaseEffect() // 清空全局副作用函数
      this.proxyWindow.microApp.clearDataListener()
      // TODO: 基座也依赖于propertyKeyWhiteList，是否会出问题
      if (--SandBox.activeCount === 0) {
        releaseEffectDocumentEvent()
        // propertyKeyWhiteList.forEach((key: PropertyKey) => {
        //   if (this.proxyWindow.hasOwnProperty(key)) {
        //     // @ts-ignore
        //     delete window[key]
        //   }
        // })
      }
    }
  }

  // 获取隔离属性
  // 隔离属性是指无法兜底到外层的属性，完全和基座window隔离
  getScopeProperties (appName: string): any[] {
    let properties: string[] = []
    if (typeof microApp.plugins !== 'object') return properties

    if (toString.call(microApp.plugins.global) === '[object Array]') {
      for (const plugin of microApp.plugins.global!) {
        if (typeof plugin === 'object' && toString.call(plugin.scopeProperties) === '[object Array]') {
          properties = properties.concat(plugin.scopeProperties!)
        }
      }
    }

    if (toString.call(microApp.plugins.modules?.[appName]) === '[object Array]') {
      for (const plugin of microApp.plugins.modules![appName]) {
        if (typeof plugin === 'object' && toString.call(plugin.scopeProperties) === '[object Array]') {
          properties = properties.concat(plugin.scopeProperties!)
        }
      }
    }

    return properties
  }

  // 注入window的全局变量
  inject (clonedWindow: clonedWindowType, appName: string, url: string): void {
    clonedWindow.__micro_app_environment__ = true
    clonedWindow.__micro_app_name__ = appName
    // 运行时public-path是对publicPath的替换
    clonedWindow.__full_public_path__ = getEffectivePath(url)
    clonedWindow.microApp = new EventCenterForMicroApp(appName)
    clonedWindow.rawWindow = window
    clonedWindow.rawDocument = document
  }
}
